import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject, lastValueFrom, map } from "rxjs";
import { AppData } from "../helpers/app-data";
import {
  ApiResponse,
  ApiResponseDataTable,
} from "../interfaces/generalInterfaces";
import { ModalNotifyTypes } from "../components/modal-notify/modal-notify.component";
import { data } from "jquery";

@Injectable({
  providedIn: "root",
})
export class BaseServiceService {
  private _refresh$ = new Subject<void>();

  constructor(private http: HttpClient) {}

  /* Logica para manejar los botones a mostrar en command-bar-component */
  private buttonsToShowSubject: BehaviorSubject<string[]> = new BehaviorSubject<
    string[]
  >(["New"]); // Valor por defecto
  public buttonsToShow$: Observable<string[]> =
    this.buttonsToShowSubject.asObservable();

  async setButtonsToShow(buttons: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.buttonsToShowSubject.next(buttons);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  /* --------------------*   Peticiones CRUD   *------------------------- */

  //Obtener datatables
  async getDataTable<T>(resource: string): Promise<ApiResponseDataTable> {
    try {
      const data = await lastValueFrom(
        this.http.get<ApiResponseDataTable>(`${AppData.API_URL}${resource}`)
      );
      return data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  // Función para obtener todos los elementos
  getAll(resource: string): Observable<any> {
    try {
      const data = this.http.get<any>(`${AppData.API_URL}${resource}`);
      return data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  // Función para obtener un elemento por su ID
  async getById(resource: string, id: number): Promise<any> {
    try {
      const data = await lastValueFrom(
        this.http.get<any>(`${AppData.API_URL}${resource}?Id=${id}`)
      );
      return data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  // Función para crear un nuevo elemento
  async create(resource: string, data: any): Promise<any> {
    try {
      const createdItem = await lastValueFrom(
        this.http.post<any>(`${AppData.API_URL}${resource}`, data)
      );
      return createdItem;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  async createTarea(resource: string, data: any): Promise<any> {
    try {
      const formData = new FormData();
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          formData.append(key, data[key]);
        }
      }

      const headers = new HttpHeaders();
      headers.set("Content-Type", "multipart/form-data");
      const createdItem = await lastValueFrom(
        this.http.post<any>(`${AppData.API_URL}${resource}`, formData, {
          headers: headers,
        })
      );
      return createdItem;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  // Función para actualizar un elemento existente
  async update(resource: string, id: number, data: any): Promise<any> {
    try {
      const updatedItem = await lastValueFrom(
        this.http.post<any>(`${AppData.API_URL}${resource}?Id=${id}`, data)
      );
      return updatedItem;
    } catch (error) {
      //this.handleError(error);
      this.triggerShowModal(
        "Error al actualizar Formulario",
        error,
        ModalNotifyTypes.Danger,
        false
      );
      throw error;
    }
  }

  // Función para eliminar un elemento
  async delete(resource: string, id: number): Promise<void> {
    try {
      return await lastValueFrom(
        this.http.post<any>(`${AppData.API_URL}${resource}?Id=${id}`, {
          Id: id,
          active: false,
        })
      );
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  // Manejo de errores
  private handleError(error: any): void {
    if (error instanceof ErrorEvent) {
      // Error del lado del cliente
      console.error("An error occurred:", error.message);
    } else {
      // El servidor devolvió un código de error
      console.error(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`
      );
    }
  }

  /* Acciones DataTable */

  //Generar la columna opciones
  generateOptionsColumn(): any {
    return {
      title: "Opciones",
      data: "Id",
      orderable: false,
      render: (data: any, type: any, full: any) => {
        return `
          <button class="ms-CommandButton-button"  data-id="${full.Id}" id="btnEdit">
            <span class="ms-CommandButton-icon ms-fontColor-themePrimary"><i class="ms-Icon ms-Icon--EditNote"></i></span>
          </button>
          <button class="ms-CommandButton-button" type="button" id="btnDelete" data-id="${full.Id}">
            <span class="ms-CommandButton-icon ms-fontColor-themePrimary"><i class="ms-Icon ms-Icon--Delete" ></i></span>
          </button>
        `;
      },
    };
  }

  get refresh$() {
    return this._refresh$;
  }

  clearForm() {
    // Limpia los valores de los inputs del formulario
    const inputs = document.querySelectorAll(".text-Create");
    inputs.forEach((input) => {
      (input as HTMLInputElement).value = "";
    });

    //limpia los valores de los select
    const selects = document.querySelectorAll(".ms-Dropdown-select");
    const selectedOption = document.querySelectorAll(".ms-Dropdown-title");
    selects.forEach((select) => {
      (select as HTMLSelectElement).selectedIndex = 0;

      (select as HTMLSelectElement).value = (
        select as HTMLSelectElement
      ).options[0].innerText;
      selectedOption.forEach((selected) => {
        (selected as HTMLSelectElement).innerText = (
          select as HTMLSelectElement
        ).options[0].innerText;
      });
    });
  }

  //Enviar id desde DataTable, recibir datos y editar desde componente
  private idSeleccionado: number | null = null;

  setIdSeleccionado(id: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.idSeleccionado = id;
      resolve();
    });
  }

  getIdSeleccionado(): Promise<number | null> {
    return new Promise<number | null>((resolve, reject) => {
      resolve(this.idSeleccionado);
    });
  }

  getEstadoText(estado: number) {
    switch (estado) {
      case 1:
        return "Diseño";

      case 2:
        return "Pruebas";

      case 3:
        return "Produccion";

      default:
        break;
    }
  }
  /* Fin Acciones Datatable */

  /* Observables */
  getRoles(): Observable<any[]> {
    return this.http
      .get<ApiResponse>(AppData.API_URL + "RolUsuario/ListaRolesActivos")
      .pipe(map((response) => response.Data));
  }

  private showModalSubject = new Subject<{
    titulo: string;
    descripcion: string;
    modalNotifyType: any;
    isDelete: boolean;
  }>();

  showModal$ = this.showModalSubject.asObservable();

  triggerShowModal(
    titulo: string,
    descripcion: string,
    modalNotifyType: any,
    isDelete: boolean
  ) {
    this.showModalSubject.next({
      titulo,
      descripcion,
      modalNotifyType,
      isDelete,
    });
  }

  private abrirModalEditarSource = new Subject<void>();
  abrirModalEditar$ = this.abrirModalEditarSource.asObservable();

  abrirModalEditar(): void {
    this.abrirModalEditarSource.next();
  }

  private descargarDiagrama = new Subject<void>();
  descargarDiagrama$ = this.descargarDiagrama.asObservable();
  descargarDiagramas() {
    this.descargarDiagrama.next();
  }

  private importarDiagrama = new Subject<{ xml: string }>();
  importarDiagrama$ = this.importarDiagrama.asObservable();
  importarFiagramas(xml: string) {
    this.importarDiagrama.next({ xml });
  }

  private processIdSource = new BehaviorSubject<string | null>(null);
  currentUserId = this.processIdSource.asObservable();

  exportIDProcess(processId: string) {
    this.processIdSource.next(processId);
  }

  private formIdSource = new BehaviorSubject<string | null>(null);
  currentFormId = this.formIdSource.asObservable();

  exportIDForm(formId: string) {
    this.formIdSource.next(formId);
  }

  private idEjecucionProcesoSource = new BehaviorSubject<string | null>(null);
  currentIdEjecucionProceso = this.idEjecucionProcesoSource.asObservable();

  exportIdEjecucionProceso(idEjecucion: string) {
    this.idEjecucionProcesoSource.next(idEjecucion);
  }

  private idgrupoSource = new BehaviorSubject<string | null>(null);
  currentIdGrupo = this.idgrupoSource.asObservable();

  exportIdGrupo(idgrupo: string) {
    this.idgrupoSource.next(idgrupo);
  }

  private idOpcionMenuSource = new BehaviorSubject<string | null>(null);
  currentIdOpcionMenu = this.idOpcionMenuSource.asObservable();

  exportIdOpcionMenu(idOpcionMenu: string) {
    this.idOpcionMenuSource.next(idOpcionMenu);
  }

  private idEjecucionTarea = new BehaviorSubject<string | null>(null);
  currentIdEjecucionTarea = this.idEjecucionTarea.asObservable();

  exportIdEjecucionTarea(idEjecucionTarea: string) {
    this.idEjecucionTarea.next(idEjecucionTarea);
  }

  private isOptionsMenuSource = new BehaviorSubject<boolean | null>(null);
  currentIsOptionsMenu = this.isOptionsMenuSource.asObservable();

  exportIsOptionMenu(idOptionMenu: boolean) {
    this.isOptionsMenuSource.next(idOptionMenu);
  }

  private tablaArchivosVisible = new BehaviorSubject<boolean>(true);
  currentTablaArchivosVisible = this.tablaArchivosVisible.asObservable();

  exportVisibleTablaArchivos(visible: boolean) {
    this.tablaArchivosVisible.next(visible);
  }

  private isRedirectAccessSource = new BehaviorSubject<boolean>(false);
  isRedirectAccess$ = this.isRedirectAccessSource.asObservable();

  setRedirectAccess(isRedirect: boolean) {
    this.isRedirectAccessSource.next(isRedirect);
  }

  private usernameSource = new BehaviorSubject<string | null>(null);
  currentUsername = this.usernameSource.asObservable();

  setUsername(username: string) {
    this.usernameSource.next(username);
  }

  private userRolSource = new BehaviorSubject<string | null>(null);
  currentUserRol = this.userRolSource.asObservable();

  setUserRol(roluser: string) {
    this.userRolSource.next(roluser);
  }

  private userValidationType = new BehaviorSubject<string | null>(null);
  currentUserValidationType = this.userValidationType.asObservable();

  setUserValidationType(typeValidation: string) {
    this.userValidationType.next(typeValidation);
  }

  private userMenuPermissions = new BehaviorSubject<string | null>(null);
  currentUserPermissions = this.userMenuPermissions.asObservable();

  setUserPermissions(userpermissions: string) {
    this.userMenuPermissions.next(userpermissions);
  }

  private idsExcluidosUsuarios = new BehaviorSubject<[] | null>(null);
  currentIdsExcluidosUsuarios = this.idsExcluidosUsuarios.asObservable();

  exportIdsExcluidosUsuarios(IdsExcluidos: []) {
    this.idsExcluidosUsuarios.next(IdsExcluidos);
  }

  private idsExcluidosRoles = new BehaviorSubject<[] | null>(null);
  currentIdsExcluidosRoles = this.idsExcluidosRoles.asObservable();

  exportIdsExcluidosRoles(IdsExcluidos: []) {
    this.idsExcluidosRoles.next(IdsExcluidos);
  }

  private idsExcluidosGrupos = new BehaviorSubject<[] | null>(null);
  currentIdsExcluidosGrupos = this.idsExcluidosGrupos.asObservable();

  exportIdsExcluidosGrupos(IdsExcluidos: []) {
    this.idsExcluidosGrupos.next(IdsExcluidos);
  }

  /* Fin Observables */

  getParamValue(paramName) {
    var url = window.location.search.substring(1); //get rid of "?" in querystring
    var qArray = url.split("&"); //get key-value pairs
    for (var i = 0; i < qArray.length; i++) {
      var pArr = qArray[i].split("="); //split key and value
      if (pArr[0] == paramName) return pArr[1]; //return value
    }
  }

  async descargarReporte(IdEjecucion: number, controlador:string, metodo:string, nombreArchivo:string) {
    try {
      const response = await lastValueFrom(
        this.http.post<Blob>(
          `${AppData.API_URL}${controlador}/${metodo}?IdEjecucion=${IdEjecucion}`,
          {},
          { responseType: "blob" as "json" }
        )
      );

      // Manejar la descarga del archivo
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${nombreArchivo}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al generar el archivo Excel:", error);
    }
  }

  onInputChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement.valueAsNumber < 0) {
      inputElement.value = '0'; // Restablece a 0 si el valor es negativo
    }
  }

  isFirefox(): boolean {
    return /Firefox/.test(navigator.userAgent);
  }
}
